/**
 * threadNotifyPresentation — the three notification states of a thread, as
 * glyph + words, in ONE table (header rail tiers, 2026-09-15).
 *
 * The bell used to live only in the wide header rail (ThreadHeaderActionButtons),
 * so a phone — whose rail is [☰][⋯][hide] — and the docked panel had NO way to
 * change a thread's notifications at all (the 2026-08-27 design record listed
 * "docked-density overflow menu does not yet carry the bell" as an open
 * follow-up). The bell now also renders as an item in the ⋯ menu, and the two
 * surfaces read this one table so they can never name the same state
 * differently. UI chrome copy (D9 — status, never content).
 */
import React from 'react';
import type { ThreadNotifyMode } from '../../services/threadNotifications';

/**
 * Bell tooltip suffix when the OS/browser has notifications BLOCKED for this
 * site (platform-aware permission, 2026-09-08): the 'chime-platform' state
 * cannot deliver, and saying so here is the one place the user is looking.
 * Points at the Settings card.
 */
export const OS_NOTIFICATIONS_BLOCKED_HINT =
  'os notifications are blocked for prest0 in your browser or device settings — fix it under settings → notifications';

/** Bell glyphs for the three notification states (owner directive 2026-08-26). */
export const BellChimeIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path>
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
  </svg>
);

export const BellPlatformIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path>
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
    {/* ring waves — the "and a platform message" state */}
    <path d="M2.5 8a9.5 9.5 0 0 1 2-5"></path>
    <path d="M21.5 8a9.5 9.5 0 0 0-2-5"></path>
  </svg>
);

export const BellOffIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8.7 3A6 6 0 0 1 18 8c0 4.5 1.2 6.9 2.2 8.2"></path>
    <path d="M17 17H3s3-2 3-9a6 6 0 0 1 .3-1.9"></path>
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
    <line x1="2" y1="2" x2="22" y2="22"></line>
  </svg>
);

export interface NotifyModePresentation {
  icon: React.ReactNode;
  /** The rail button's tooltip / accessible name: state + what a click does. */
  title: string;
  /** The ⋯ menu item's short label: state only (the item IS the click). */
  menuLabel: string;
}

export const NOTIFY_MODE_PRESENTATION: Record<ThreadNotifyMode, NotifyModePresentation> = {
  chime: {
    icon: <BellChimeIcon />,
    title: 'notifications: quick chime on new messages — click for chime + platform notifications',
    menuLabel: 'notifications: chime',
  },
  'chime-platform': {
    icon: <BellPlatformIcon />,
    title: 'notifications: chime + platform notification per new message — click to turn notifications off',
    menuLabel: 'notifications: chime + platform',
  },
  off: {
    icon: <BellOffIcon />,
    title: 'notifications: off for this thread — click for the quick chime',
    menuLabel: 'notifications: off',
  },
};

/** The full tooltip for a mode, with the OS-blocked hint appended when it applies. */
export function notifyModeTitle(mode: ThreadNotifyMode, osNotificationsBlocked?: boolean): string {
  return `${NOTIFY_MODE_PRESENTATION[mode].title}${osNotificationsBlocked ? ` · ${OS_NOTIFICATIONS_BLOCKED_HINT}` : ''}`;
}
